import { useListChatProfilesQuery } from '@platform/api';
import { useMockChatStream, useStatelessChat } from '@platform/chat';
import { chatMessageEntrypoint } from '@platform/chat/messageEntrypoint';
import type { MessageEntity } from '@platform/timeline';
import {
  addEntity,
  clearConversation,
  filterByMode,
  migrateConversation,
  selectActiveConvId,
  selectActiveEntities,
  setActiveConversation,
  Timeline,
} from '@platform/timeline';
import {
  Button,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@shared/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { MockWs } from '../../services/chatWs.mock';
import type { RootState } from '../../store/store';

export interface ChatPageProps {
  // Optional: help text to display in the UI
  helpText?: string;
  // Optional: mock WebSocket for streaming (Storybook only)
  mockWs?: MockWs;
  // Optional: conversation ID (for testing)
  convId?: string;
  // Optional: callback for testing
  onSendMessage?: (message: string) => void;
}

export default function ChatPage({
  helpText,
  mockWs,
  convId: propsConvId,
  onSendMessage,
}: ChatPageProps = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [showHelp, setShowHelp] = useState(!!helpText);
  const [debugMode, setDebugMode] = useState(false);
  const [localConvId, setLocalConvId] = useState<string | null>(null);
  const initialConvIdRef = useRef(`conv-${Date.now()}`);

  // Get or create conversation ID
  const storeConvId = useSelector(selectActiveConvId);
  const convId = propsConvId || localConvId || storeConvId || initialConvIdRef.current;

  // Set active conversation on mount
  useEffect(() => {
    dispatch(setActiveConversation(convId));
  }, [dispatch, convId]);

  // Get auth token to conditionally fetch profiles and enable WebSocket
  const token = useSelector((s: RootState) => s.auth.token);

  // Connect mock WebSocket if provided (for Storybook testing)
  useMockChatStream({
    mockWs: mockWs!,
    enabled: !!mockWs,
  });

  // Stateless SSE chat hook (no WebSocket fallback)
  const {
    isRunning,
    error: chatError,
    sendMessage,
    serverConvId,
  } = useStatelessChat({
    convId,
    profile: selectedProfile || undefined,
    enabled: !mockWs && !!selectedProfile && !!token,
  });

  // Update local convId if server assigns one
  useEffect(() => {
    if (serverConvId && serverConvId !== convId && serverConvId !== localConvId) {
      dispatch(migrateConversation({ fromConvId: convId, toConvId: serverConvId }));
      setLocalConvId(serverConvId);
      dispatch(setActiveConversation(serverConvId));
    }
  }, [serverConvId, convId, localConvId, dispatch]);

  // Get entities from Redux
  const allEntities = useSelector(selectActiveEntities);

  // Filter entities based on debug mode
  // Hide status widgets and unknown entity types (like turn final logs, middleware events) unless debug is enabled
  const entities = debugMode
    ? filterByMode(allEntities, 'debug')
    : filterByMode(allEntities, 'normal');
  const { data: profilesData, isLoading: profilesLoading } = useListChatProfilesQuery(undefined, {
    skip: !token, // Skip query if no token available
  });

  const handleNewChat = useCallback(() => {
    // Clear the current conversation from Redux
    if (convId) {
      dispatch(clearConversation(convId));
    }
    // Create a new conversation ID
    const newConvId = `conv-${Date.now()}`;
    setLocalConvId(newConvId);
    dispatch(setActiveConversation(newConvId));
  }, [convId, dispatch]);

  const handleSubmit = useCallback(async () => {
    if (!prompt.trim()) return;

    // Add user message entity to Redux
    const userMessage: MessageEntity = {
      id: `user-${Date.now()}`,
      kind: 'message',
      timestamp: Date.now(),
      props: {
        role: 'user',
        content: prompt,
      },
    };
    dispatch(addEntity({ convId, entity: userMessage }));

    // Call callback if provided (for testing/stories)
    onSendMessage?.(prompt);

    // Use stateless SSE flow
    try {
      await sendMessage({
        text: prompt,
        messageEntrypoint: chatMessageEntrypoint.typedInput,
      });
    } catch (err) {
      // Error already handled by the hook
      console.error('Failed to send message via SSE:', err);
    }

    // Clear input
    setPrompt('');
  }, [prompt, convId, sendMessage, onSendMessage, dispatch]);

  const profiles = profilesData?.profiles || [];
  // Normalize profile options to strings (backend may return objects with slug)
  const profileOptions = profiles
    .map((p: any) =>
      typeof p === 'string'
        ? { value: p, label: p }
        : { value: p?.slug ?? '', label: p?.slug ?? '' }
    )
    .filter((p) => p.value);

  // Sort profiles: "default" first, then alphabetically
  const sortedProfileOptions = [...profileOptions].sort((a, b) => {
    // "default" always comes first
    if (a.value === 'default') return -1;
    if (b.value === 'default') return 1;
    // Otherwise sort alphabetically
    return a.value.localeCompare(b.value);
  });

  // Set default profile when profiles are loaded or when selected profile becomes invalid
  useEffect(() => {
    if (sortedProfileOptions.length > 0) {
      // If no profile is selected, or the selected profile is no longer available
      if (!selectedProfile || !sortedProfileOptions.find((p) => p.value === selectedProfile)) {
        // Always prefer "default" if it exists, otherwise use first profile
        const defaultProfile = sortedProfileOptions.find((p) => p.value === 'default');
        setSelectedProfile(defaultProfile?.value || sortedProfileOptions[0].value);
      }
    }
  }, [sortedProfileOptions, selectedProfile]);

  function toggleDebugOnly() {
    setDebugMode(!debugMode);
  }

  // Compute busy state
  const isCurrentlyBusy = isRunning;

  return (
    <div className="flex flex-col gap-4 max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between">
        <Typography variant="h4">Chat (Proof of Concept)</Typography>
        <div className="flex items-center gap-3">
          <Button
            size="small"
            variant="outlined"
            onClick={handleNewChat}
            startIcon={<Icon>add_comment</Icon>}
            disabled={entities.length === 0}
          >
            New Chat
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/chat/sidebar')}
            startIcon={<Icon>side_navigation</Icon>}
          >
            Sidebar Mode
          </Button>
          <Button
            size="small"
            variant={debugMode ? 'contained' : 'outlined'}
            onClick={toggleDebugOnly}
            startIcon={<Icon>{debugMode ? 'bug_report' : 'visibility_off'}</Icon>}
          >
            Debug {debugMode ? 'ON' : 'OFF'}
          </Button>
          {profilesLoading && <LinearProgress style={{ width: 100 }} />}
        </div>
      </div>

      {/* Error display */}
      {chatError && (
        <Paper className="p-4 bg-red-50 border-l-4 border-red-500">
          <div className="flex items-start gap-2">
            <Icon sx={{ fontSize: 20, color: 'error.main' }}>error</Icon>
            <Typography variant="body2" color="error">
              {chatError}
            </Typography>
          </div>
        </Paper>
      )}

      {/* Help text (if provided) */}
      {helpText && showHelp && (
        <Paper className="p-4 bg-blue-50 border-l-4 border-blue-500">
          <div className="flex items-start gap-2">
            <Icon sx={{ fontSize: 20, color: 'info.main' }}>info</Icon>
            <div className="flex-1">
              <Typography variant="subtitle2" className="mb-2">
                💡 Try These Commands
              </Typography>
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p className="mb-2 last:mb-0 text-sm">{children}</p>,
                    strong: ({ children }) => (
                      <strong className="font-semibold text-blue-700">{children}</strong>
                    ),
                    code: ({ children }) => (
                      <code className="bg-blue-100 px-1 py-0.5 rounded text-xs font-mono">
                        {children}
                      </code>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc ml-4 mb-2 text-sm">{children}</ul>
                    ),
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                  }}
                >
                  {helpText}
                </ReactMarkdown>
              </div>
            </div>
            <IconButton size="small" onClick={() => setShowHelp(false)}>
              <Icon>close</Icon>
            </IconButton>
          </div>
        </Paper>
      )}

      {/* Profile selector */}
      <Paper className="p-4">
        <FormControl fullWidth>
          <InputLabel>Profile</InputLabel>
          <Select
            value={selectedProfile || ''}
            label="Profile"
            onChange={(e) => setSelectedProfile(e.target.value as string)}
            disabled={profilesLoading || sortedProfileOptions.length === 0}
          >
            {sortedProfileOptions.length === 0 && !profilesLoading && (
              <MenuItem value="" disabled>
                No profiles available
              </MenuItem>
            )}
            {sortedProfileOptions.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Timeline */}
      {entities.length > 0 && (
        <Paper className="p-4">
          <Typography variant="subtitle2" className="mb-3">
            Conversation
          </Typography>
          <Timeline entities={entities} autoScroll={true} debugMode={debugMode} convId={convId} />
        </Paper>
      )}

      {/* Input */}
      <Paper className="p-4">
        <div className="flex flex-col gap-3">
          <TextField
            label="Prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Ask something..."
            multiline
            rows={4}
            fullWidth
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isCurrentlyBusy || !prompt.trim()}
              startIcon={<Icon>send</Icon>}
            >
              {isCurrentlyBusy ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </div>
      </Paper>

      {/* Response + Error panels removed; queue is fire-and-forget */}
    </div>
  );
}
